import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useUserCourse } from '../../hooks/queries/useUserCourses'
import { useEffect, useRef } from 'react'
import { setPresentStatus } from '../../helpers/userAttendance'
import { setNewAccessToken } from '../../infrastructure/apis'
import { useCourseStudyPlan } from '../../hooks/useCourseStudyPlan'
import { useCourseNavigation } from '../../hooks/useCourseNavigation'
import { CourseContentList } from './CourseContentList'
import { useOpenMeet } from '../../hooks/useOpenMeet'

export const CourseManager = () => {
    const { slugOrId } = useParams()
    const { courseId, course, status } = useUserCourse(slugOrId)
    const navigate = useNavigate()
    const [params] = useSearchParams()
    const hasOpenMeet = params.get('meet')
    const hasStep = params.get('step')
    const { profileId, navigateToCourse } = useCourseNavigation()

    const { openMeet } = useOpenMeet(courseId)
    const { firstStepId } = useCourseStudyPlan(courseId)

    // Set course access token and mark present when course becomes available.
    const attendanceMarkedRef = useRef(false)

    useEffect(() => {
        if (slugOrId === 'undefined') {
            navigate('/home')
            return
        }

        if (!course) return
        if (attendanceMarkedRef.current) return

        // Set access token if available (idempotent) and clean the URL
        if (course['access-token']) {
            try {
                setNewAccessToken(course['access-token'])
            } catch (e) {
                console.error('Failed to set access token:', e)
                // swallow to avoid blocking attendance; token set helper should handle logging
            }
        }
        // Clean temporary params (meet, step) but preserve profileId for cohort disambiguation
        const cleanUrl = profileId
            ? `${window.location.pathname}?profileId=${profileId}`
            : window.location.pathname
        window.history.replaceState({}, '', cleanUrl)

        try {
            setPresentStatus(course)
        } catch (err) {
            console.error('Failed to set present status:', err)
            // non-fatal: continue flow
        }

        attendanceMarkedRef.current = true

        if (hasOpenMeet) openMeet()

        // Intentionally include stable references we rely on.
    }, [course, slugOrId, hasOpenMeet, navigate, openMeet, profileId])

    // Open first step if hasStep is true
    useEffect(() => {
        if (!course || !hasStep || !firstStepId) return
        if (slugOrId) {
            navigateToCourse(slugOrId, firstStepId)
        }
    }, [course, hasStep, firstStepId, slugOrId, navigateToCourse])

    if (status === 'success' && !course) navigate('/home')

    return <CourseContentList slugOrId={slugOrId} />
}
